import { StyleSheet, css } from 'aphrodite';
import { type FC } from 'react';
import { isLoadedSnapshot, type DialPacingSnapshot } from './types';

export type PacingStripProps = {
	snapshot: DialPacingSnapshot;
	variant: 'top' | 'compact';
};

export const PacingStrip: FC<PacingStripProps> = ({ snapshot, variant }) => {
	if (!isLoadedSnapshot(snapshot)) {
		return <SkeletonChips variant={variant} />;
	}

	const dialsLabel =
		snapshot.dialGoal != null ? `${snapshot.dialsToday} / ${snapshot.dialGoal}` : `${snapshot.dialsToday} dials`;

	if (variant === 'compact') {
		return (
			<div className={css(styles.compact)}>
				<span className={css(styles.chip)}>{dialsLabel}</span>
				{snapshot.dialGoal != null && <ProgressBar value={snapshot.dialsToday} max={snapshot.dialGoal} />}
			</div>
		);
	}

	return (
		<div className={css(styles.top)}>
			<span className={css(styles.chip)}>{dialsLabel}</span>
			<span className={css(styles.chip)}>{snapshot.connectsToday} connects</span>
			<span className={css(styles.chip)}>
				{snapshot.demosBookedToday} {snapshot.demosBookedToday === 1 ? 'demo' : 'demos'}
			</span>
			{snapshot.dialGoal != null && <ProgressBar value={snapshot.dialsToday} max={snapshot.dialGoal} />}
		</div>
	);
};

const SkeletonChips: FC<{ variant: 'top' | 'compact' }> = ({ variant }) => (
	<div data-testid='pacing-skeleton' className={css(variant === 'top' ? styles.top : styles.compact)}>
		<span className={css(styles.chipSkeleton)} />
		{variant === 'top' && (
			<>
				<span className={css(styles.chipSkeleton)} />
				<span className={css(styles.chipSkeleton)} />
			</>
		)}
	</div>
);

const ProgressBar: FC<{ value: number; max: number }> = ({ value, max }) => {
	const pct = Math.max(0, Math.min(100, (value / Math.max(1, max)) * 100));
	return (
		<div className={css(styles.progressTrack)} role='progressbar' aria-valuenow={value} aria-valuemax={max}>
			<div className={css(styles.progressFill)} style={{ width: `${pct}%` }} />
		</div>
	);
};

const styles = StyleSheet.create({
	top: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	compact: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	chip: {
		fontSize: 12,
		fontWeight: 600,
		color: 'var(--ink)',
		background: 'var(--bg-elev)',
		padding: '4px 10px',
		borderRadius: 999,
		border: '1px solid var(--border)',
	},
	chipSkeleton: {
		display: 'inline-block',
		width: 64,
		height: 22,
		borderRadius: 999,
		background: 'var(--bg-elev)',
		opacity: 0.6,
	},
	progressTrack: {
		flex: 1,
		height: 4,
		background: 'var(--bg-elev)',
		borderRadius: 2,
		overflow: 'hidden',
	},
	progressFill: {
		height: '100%',
		background: 'var(--accent)',
		transition: 'width 200ms ease-out',
	},
});
